/**
 * useClaimPendingPurchase
 *
 * Global hook that runs after authentication to claim any pending
 * quick-predict purchase that was made as a guest.
 *
 * Problem: when a guest buys + runs a prediction and then signs in
 * (especially via Google OAuth), the browser does a full-page redirect.
 * The recovery effects that save the report only live inside
 * CustomPrediction.jsx, so if the user lands on any other page after
 * login the report is never persisted to their account.
 *
 * This hook reads the pending save payload from localStorage on every
 * page and, once the user is authenticated, links the purchase and
 * saves the report.
 */

import { useEffect, useRef } from 'react'
import { useAuth } from './useAuth'
import { usePremium } from './usePremium'
import { savePrediction } from '../services/savedPredictions'
import { linkGuestQuickPredict } from '../services/stripe'

const PENDING_SAVE_KEY = 'pending_quick_predict_saved_prediction_payload'
const CLAIM_LOCK_KEY = 'pending_quick_predict_claim_lock'

function readPendingPayload() {
  try {
    const raw = localStorage.getItem(PENDING_SAVE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !parsed.payload || typeof parsed.payload !== 'object') {
      return null
    }
    return parsed.payload
  } catch (_) {
    return null
  }
}

function clearPendingPayload() {
  try {
    localStorage.removeItem(PENDING_SAVE_KEY)
  } catch (_) {}
}

/**
 * Acquire a synchronous lock so only one tab / effect processes the
 * pending payload.  The lock is stored in sessionStorage with a short
 * TTL so a stale lock from a crashed tab doesn't block forever.
 */
function acquireClaimLock() {
  try {
    const existing = sessionStorage.getItem(CLAIM_LOCK_KEY)
    if (existing) {
      const ts = Number(existing)
      if (Date.now() - ts < 30_000) return false
    }
    sessionStorage.setItem(CLAIM_LOCK_KEY, String(Date.now()))
    return true
  } catch (_) {
    return true
  }
}

function releaseClaimLock() {
  try { sessionStorage.removeItem(CLAIM_LOCK_KEY) } catch (_) {}
}

export function useClaimPendingPurchase() {
  const { isAuthenticated, user } = useAuth()
  const { refreshCredits } = usePremium()
  const attemptedRef = useRef(false)

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return
    if (attemptedRef.current) return

    const payload = readPendingPayload()
    if (!payload) return
    if (!acquireClaimLock()) return

    attemptedRef.current = true

    ;(async () => {
      const stripeSessionId = payload.stripe_session_id || payload.stripeSessionId || null
      let claimed = false

      // First try the full link path (grants prediction + PDF credits + claims pending report).
      if (stripeSessionId) {
        try {
          const linkResult = await linkGuestQuickPredict(stripeSessionId, user.id)
          if (linkResult && !linkResult.error && linkResult.reportSaved) {
            clearPendingPayload()
            claimed = true
          }
        } catch (_) {
          // Fall through to direct save
        }
      }

      // Fallback: save the report directly via the saved-predictions API.
      if (!claimed) {
        try {
          const result = await savePrediction(payload)
          if (result?.success) {
            clearPendingPayload()
            claimed = true
          }
        } catch (_) {
          // Non-fatal; the payload stays in localStorage for the next attempt
          // (e.g. when the user visits /predict).
        }
      }

      if (claimed) refreshCredits()
      releaseClaimLock()
    })()
  }, [isAuthenticated, user, refreshCredits])
}
